/**
 * Column Type Selector
 *
 * User-friendly dropdown for selecting column data types.
 * Shows icons and descriptions for each type.
 */

import React from "react";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import ListItemDecorator from "@mui/joy/ListItemDecorator";
import Typography from "@mui/joy/Typography";
import Box from "@mui/joy/Box";
import TextFieldsRoundedIcon from "@mui/icons-material/TextFieldsRounded";
import NumbersRoundedIcon from "@mui/icons-material/NumbersRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import ListRoundedIcon from "@mui/icons-material/ListRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import type { SimpleColumnType } from "../../types";

interface ColumnTypeSelectorProps {
  value: SimpleColumnType;
  onChange: (value: SimpleColumnType) => void;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
}

interface TypeOption {
  value: SimpleColumnType;
  label: string;
  description: string;
  icon: React.ReactNode;
}

const TYPE_OPTIONS: TypeOption[] = [
  {
    value: "Text",
    label: "Text",
    description: "Letters, words, sentences",
    icon: <TextFieldsRoundedIcon fontSize="small" />,
  },
  {
    value: "Number",
    label: "Number",
    description: "Whole numbers or decimals",
    icon: <NumbersRoundedIcon fontSize="small" />,
  },
  {
    value: "Date",
    label: "Date",
    description: "Calendar dates",
    icon: <EventRoundedIcon fontSize="small" />,
  },
  {
    value: "Yes/No",
    label: "Yes/No",
    description: "True or false values",
    icon: <CheckBoxRoundedIcon fontSize="small" />,
  },
  {
    value: "Selection",
    label: "Selection",
    description: "Choose from a list",
    icon: <ListRoundedIcon fontSize="small" />,
  },
  {
    value: "File",
    label: "File",
    description: "Upload documents and files",
    icon: <AttachFileRoundedIcon fontSize="small" />,
  },
];

export function ColumnTypeSelector({
  value,
  onChange,
  disabled,
  size = "sm",
}: ColumnTypeSelectorProps) {
  const selectedOption = TYPE_OPTIONS.find((opt) => opt.value === value);

  return (
    <Select
      size={size}
      value={value}
      onChange={(_, newValue) =>
        newValue && onChange(newValue as SimpleColumnType)
      }
      disabled={disabled}
      startDecorator={selectedOption?.icon}
      renderValue={() => selectedOption?.label || value}
      sx={{ width: 130 }}
      slotProps={{
        listbox: {
          sx: { minWidth: 220 },
        },
      }}
    >
      {TYPE_OPTIONS.map((option) => (
        <Option key={option.value} value={option.value}>
          <ListItemDecorator>{option.icon}</ListItemDecorator>
          <Box>
            <Typography level="body-sm">{option.label}</Typography>
            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
              {option.description}
            </Typography>
          </Box>
        </Option>
      ))}
    </Select>
  );
}

export default ColumnTypeSelector;
